import React, { useState } from 'react';
import { 
  Users, Stethoscope, Activity, Heart, Clock, Play, Square, 
  CheckCircle2, AlertTriangle, FileText, Send, Sparkles, TrendingUp, Printer
} from 'lucide-react';
import { DEMO_STUDENTS, DemoStudent } from '../data/demoStudents';
import { generateAndPrintClinicalReport } from '../utils/clinicalReportGenerator';
import { UserSession } from '../AppShell';

interface ClinicalCockpitPanelProps {
  currentUser?: UserSession | null;
}

export const ClinicalCockpitPanel: React.FC<ClinicalCockpitPanelProps> = ({ currentUser }) => {
  const [selectedStudent, setSelectedStudent] = useState<DemoStudent>(DEMO_STUDENTS[0]);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionSeconds, setSessionSeconds] = useState(878); // ~14m 38s
  const [abcCounters, setAbcCounters] = useState({
    antecedentDemand: 1,
    antecedentInterruption: 1,
    behaviorOffTask: 1,
    behaviorRefusal: 2,
    consequenceRedirection: 1,
    consequencePraise: 2
  });
  const [soapNotes, setSoapNotes] = useState({
    subjective: `${selectedStudent.name} was engaged today. Reported: "I feel ready to practice my words!"`,
    objective: `Completed 10/12 visual tracking tasks (83%). Reaction time averaged 180ms. Followed 2-step directions consistently.`,
    assessment: `Significant improvement in motor dwell precision and verbal mand initiation. Fatigue noted toward end of 30min session.`,
    plan: `Continue bilateral integration & core vocab matrix. Target: 15-second unassisted phrase building.`
  });
  const [claimExported, setClaimExported] = useState(false);

  const incrementCounter = (key: keyof typeof abcCounters) => {
    setAbcCounters(prev => ({ ...prev, [key]: prev[key] + 1 }));
  };

  const handleExportClaim = () => {
    setClaimExported(true);
    setTimeout(() => setClaimExported(false), 3500);
  };

  return (
    <div className="bg-[#1c1b1f] text-white p-5 sm:p-7 rounded-[36px] border border-white/10 shadow-2xl space-y-6">
      
      {/* Cockpit Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-2xl bg-[#ffd64e] text-black flex items-center justify-center font-black">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              <span>3-Pane Clinical Therapist Cockpit</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                Live HIPAA Telemetry
              </span>
            </h2>
            <p className="text-xs text-white/60">Real-time session monitoring, motor reaction curves & automated SOAP notes</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-xs font-mono text-[#ffd64e]">
            <Clock className="w-3.5 h-3.5" />
            <span>Session: {Math.floor(sessionSeconds / 60)}:{(sessionSeconds % 60).toString().padStart(2, '0')}</span>
          </div>
          <button
            onClick={() => setIsSessionActive(!isSessionActive)}
            className={`px-4 py-1.5 rounded-full text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
              isSessionActive ? 'bg-rose-600 text-white' : 'bg-[#ffd64e] text-black hover:bg-amber-400'
            }`}
          >
            {isSessionActive ? <Square className="w-3 h-3 fill-current" /> : <Play className="w-3 h-3 fill-current" />}
            <span>{isSessionActive ? 'End Session' : 'Start Live Session'}</span>
          </button>
        </div>
      </div>

      {/* 3-PANE GRID SYSTEM */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        
        {/* PANE 1: Student Clinical Caseload (Col 3) */}
        <div className="lg:col-span-3 bg-white/5 rounded-[28px] p-4 border border-white/10 space-y-3 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs px-1">
              <span className="font-bold text-white/60 uppercase tracking-wider text-[10px]">Clinical Caseload</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#ffd64e]/20 text-[#ffd64e]">
                {DEMO_STUDENTS.length} Active
              </span>
            </div>

            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 custom-scrollbar">
              {DEMO_STUDENTS.map(st => {
                const isSelected = selectedStudent.id === st.id;
                return (
                  <button
                    key={st.id}
                    onClick={() => {
                      setSelectedStudent(st);
                      setSoapNotes({
                        subjective: `${st.name} was calm and cooperative during initial sensory check-in.`,
                        objective: `Mastery at ${st.overallMasteryPercent}%. Completed tasks for ${st.disabilityCategory}.`,
                        assessment: `IEP target "${st.iepGoal}" progressing on schedule.`,
                        plan: `Continue adaptive drills with therapist ${st.therapist}.`
                      });
                    }}
                    className={`w-full p-3 rounded-2xl text-left transition-all border cursor-pointer flex items-start gap-2.5 ${
                      isSelected
                        ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-md scale-[1.01]'
                        : 'bg-white/5 text-white hover:bg-white/10 border-white/5'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-black/20 flex items-center justify-center text-sm font-black shrink-0">
                      {st.name.charAt(0)}
                    </div>
                    <div className="overflow-hidden">
                      <div className="font-black text-xs truncate">{st.name}</div>
                      <div className={`text-[10px] truncate ${isSelected ? 'text-black/70' : 'text-white/50'}`}>
                        {st.disabilityCategory}
                      </div>
                      <div className={`text-[9px] font-mono font-bold mt-0.5 ${isSelected ? 'text-black' : 'text-emerald-400'}`}>
                        Mastery: {st.overallMasteryPercent}%
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-black/40 border border-white/5 text-[10px] text-white/60">
            Selected: <strong className="text-white">{selectedStudent.name}</strong> ({selectedStudent.grade})
          </div>
        </div>

        {/* PANE 2: Active Session Telemetry & Spectrogram (Col 5) */}
        <div className="lg:col-span-5 bg-white/5 rounded-[28px] p-5 border border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-bold text-white/60 uppercase tracking-wider text-[10px]">
              Active Session Telemetry: {selectedStudent.name}
            </span>
            <span className="text-xs text-emerald-400 font-mono flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              <span>Telemetry Live</span>
            </span>
          </div>

          {/* Real-time Reaction Curve */}
          <div className="p-4 rounded-2xl bg-black/40 border border-white/10 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-white/70 font-bold flex items-center gap-1">
                <Activity className="w-3.5 h-3.5 text-[#ffd64e]" /> Motor Reaction Times
              </span>
              <span className="text-[#ffd64e] font-mono font-bold">Avg: 180ms</span>
            </div>

            {/* SVG Waveform Graph */}
            <div className="h-24 w-full flex items-end gap-1.5 pt-4">
              {[45, 60, 52, 78, 65, 90, 72, 85, 95, 80, 88, 92, 70, 60, 75, 82].map((val, i) => (
                <div key={i} className="flex-1 bg-white/10 rounded-t-sm flex flex-col justify-end h-full">
                  <div 
                    className="w-full bg-[#ffd64e] rounded-t-sm transition-all duration-500 hover:bg-white"
                    style={{ height: `${val}%` }}
                    title={`${val * 2}ms reaction latency`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[9px] text-white/40 font-mono pt-1">
              <span>0s Session Start</span>
              <span>15m Active</span>
              <span>30m Target</span>
            </div>
          </div>

          {/* Speech Waveform Spectrogram Visualizer */}
          <div className="p-4 rounded-2xl bg-black/40 border border-white/10 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-white/70 font-bold">Acoustic Speech Formant Waveform</span>
              <span className="text-xs text-amber-300 font-mono">0 - 8 kHz</span>
            </div>
            <div className="h-16 w-full rounded-xl bg-gradient-to-r from-amber-950 via-amber-600 to-yellow-400/80 p-2 flex items-center justify-center text-[10px] font-mono text-black font-black tracking-widest uppercase opacity-90 shadow-inner">
              Spectrogram Active • Pitch Match 94%
            </div>
          </div>

          {/* Vitals & Engagement Chips */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2.5 rounded-2xl bg-black/30 border border-white/5 text-center">
              <div className="text-[10px] text-white/60">Heart Rate</div>
              <div className="text-base font-black text-rose-400 font-mono flex items-center justify-center gap-1">
                <Heart className="w-3.5 h-3.5 fill-rose-500" /> 108 bpm
              </div>
            </div>
            <div className="p-2.5 rounded-2xl bg-black/30 border border-white/5 text-center">
              <div className="text-[10px] text-white/60">SpO2 Level</div>
              <div className="text-base font-black text-sky-400 font-mono">98%</div>
            </div>
            <div className="p-2.5 rounded-2xl bg-black/30 border border-white/5 text-center">
              <div className="text-[10px] text-white/60">Engagement</div>
              <div className="text-base font-black text-emerald-400 font-mono">High (88%)</div>
            </div>
          </div>
        </div>

        {/* PANE 3: 1-Click SOAP Note & ABC Counters (Col 4) */}
        <div className="lg:col-span-4 bg-white/5 rounded-[28px] p-5 border border-white/10 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-white/60 uppercase tracking-wider text-[10px]">
                SOAP Note & ABC Tracker
              </span>
              <span className="text-[10px] font-mono text-slate-400">HIPAA Certified</span>
            </div>

            {/* Quick ABC Incident Counters */}
            <div className="p-3 rounded-2xl bg-black/40 border border-white/5 space-y-2">
              <div className="text-[10px] font-black uppercase tracking-wider text-[#ffd64e]">
                1-Click ABC Behavior Incidents
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-center">
                <button
                  onClick={() => incrementCounter('antecedentDemand')}
                  className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] font-bold cursor-pointer"
                >
                  <div>Antecedent</div>
                  <div className="text-xs font-black text-[#ffd64e] font-mono">{abcCounters.antecedentDemand}</div>
                </button>
                <button
                  onClick={() => incrementCounter('behaviorRefusal')}
                  className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] font-bold cursor-pointer"
                >
                  <div>Behavior</div>
                  <div className="text-xs font-black text-rose-400 font-mono">{abcCounters.behaviorRefusal}</div>
                </button>
                <button
                  onClick={() => incrementCounter('consequencePraise')}
                  className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] font-bold cursor-pointer"
                >
                  <div>Consequence</div>
                  <div className="text-xs font-black text-emerald-400 font-mono">{abcCounters.consequencePraise}</div>
                </button>
              </div>
            </div>

            {/* SOAP Text Editor Blocks */}
            <div className="space-y-2 text-xs">
              <div>
                <label className="text-[10px] font-bold text-white/50 uppercase">Subjective (S)</label>
                <textarea
                  value={soapNotes.subjective}
                  onChange={(e) => setSoapNotes({ ...soapNotes, subjective: e.target.value })}
                  rows={2}
                  className="w-full p-2 rounded-xl bg-black/40 border border-white/10 text-white/90 text-xs focus:outline-none focus:border-[#ffd64e] resize-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-white/50 uppercase">Objective (O)</label>
                <textarea
                  value={soapNotes.objective}
                  onChange={(e) => setSoapNotes({ ...soapNotes, objective: e.target.value })}
                  rows={2}
                  className="w-full p-2 rounded-xl bg-black/40 border border-white/10 text-white/90 text-xs focus:outline-none focus:border-[#ffd64e] resize-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-white/50 uppercase">Assessment & Plan (A/P)</label>
                <textarea
                  value={`${soapNotes.assessment} ${soapNotes.plan}`}
                  onChange={(e) => setSoapNotes({ ...soapNotes, assessment: e.target.value })}
                  rows={2}
                  className="w-full p-2 rounded-xl bg-black/40 border border-white/10 text-white/90 text-xs focus:outline-none focus:border-[#ffd64e] resize-none"
                />
              </div>
            </div>
          </div>

          {/* Export Action */}
          <div className="pt-2 border-t border-white/10 space-y-2">
            <button
              onClick={() => generateAndPrintClinicalReport({
                studentName: selectedStudent.name,
                rollId: selectedStudent.rollId,
                age: selectedStudent.age,
                grade: selectedStudent.grade,
                disabilityCategory: selectedStudent.disabilityCategory,
                iepGoal: selectedStudent.iepGoal,
                sessionDuration: `${Math.floor(sessionSeconds / 60)}m ${sessionSeconds % 60}s`,
                sessionTimestamp: new Date().toLocaleDateString(),
                therapist: selectedStudent.therapist,
                tenant: currentUser?.tenant || 'AbilityOS Special Academy',
                reactionTimeAvgMs: 180,
                reactionTimePeakMs: 290,
                abcIncidents: {
                  antecedents: abcCounters.antecedentDemand + abcCounters.antecedentInterruption,
                  behaviors: abcCounters.behaviorOffTask + abcCounters.behaviorRefusal,
                  consequences: abcCounters.consequenceRedirection + abcCounters.consequencePraise
                },
                soapNotes
              })}
              className="w-full py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-xs transition cursor-pointer shadow-sm flex items-center justify-center gap-2 border border-white/15"
            >
              <Printer className="w-4 h-4 text-[#ffd64e]" />
              <span>Export Official Clinical IEP Report (PDF)</span>
            </button>

            {claimExported ? (
              <div className="p-2.5 rounded-full bg-emerald-500/20 border border-emerald-500 text-emerald-300 text-xs font-black text-center flex items-center justify-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                <span>Claim Exported (ICD-10 & NDIS Validated)</span>
              </div>
            ) : (
              <button
                onClick={handleExportClaim}
                className="w-full py-2.5 rounded-full bg-[#ffd64e] hover:bg-amber-400 text-black font-black text-xs transition cursor-pointer shadow-md flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" />
                <span>Export NDIS / Insurance Claim Note</span>
              </button>
            )}
          </div>
        </div>

      </div>

    </div>
  );
};
